// Stops a live update from re-asking an endpoint that keeps failing.
//
// A push event makes every open view that cares about it send a follow-up request. When that endpoint
// is broken for this user - billing disconnected, a permission missing, the back end failing - every
// event costs a failed request and an exception-audit row, from every open tab, for as long as the tab
// stays open. So each kind of follow-up keeps a count of its failures in a row, and once that reaches
// FAILURES_BEFORE_STOP the follow-up is not sent again until reset() - which PushEventService calls when
// its channel changes (a practice switch, another user, logout).
//
// Requests are counted by the $http decorator at the bottom of this file: any request started while
// run() is executing a follow-up counts towards it. That reaches the views' own fetch functions, which
// LiveReloadService calls without getting a promise back.
app.service("LiveUpdateGuard", [
	"$q",
	function ($q) {
		/** Failures in a row before a follow-up is stopped. A success in between starts the count again. */
		const FAILURES_BEFORE_STOP = 3;

		let failuresInARow = {};
		let stopped = {};

		/** The follow-up run() is executing, which a request started now is counted towards. */
		let currentKey = null;

		/** Bumped by reset(), so a request from before it does not count after it. */
		let generation = 0;

		const service = {
			/**
			 * Runs one follow-up, unless it has been stopped.
			 *
			 * @param key names the follow-up, e.g. "invoice". Follow-ups sharing a key share a count.
			 * @param fn  makes the request(s); whatever it returns is returned
			 * @return fn's result, or a rejected promise without calling fn once the follow-up is stopped -
			 *         callers already treat a failed follow-up as "leave the view as it is"
			 */
			run: function (key, fn) {
				if (stopped[key]) {
					return $q.reject({ liveUpdateStopped: true });
				}

				const previousKey = currentKey;
				currentKey = key;
				try {
					return fn();
				} finally {
					currentKey = previousKey;
				}
			},

			/** Whether a follow-up has failed too often in a row to be sent again. */
			isStopped: function (key) {
				return !!stopped[key];
			},

			/** Counts a request's outcome towards the follow-up running when it was started, if any. */
			track: function (promise) {
				const key = currentKey;
				if (!key || !promise || typeof promise.then !== "function") {
					return;
				}

				const requestGeneration = generation;
				promise.then(
					function () {
						if (requestGeneration === generation) {
							failuresInARow[key] = 0;
						}
					},
					function () {
						if (requestGeneration !== generation) {
							return;
						}
						failuresInARow[key] = (failuresInARow[key] || 0) + 1;
						if (failuresInARow[key] >= FAILURES_BEFORE_STOP) {
							stopped[key] = true;
						}
					}
				);
			},

			/** Lets every follow-up be sent again, with a fresh count. */
			reset: function () {
				generation++;
				failuresInARow = {};
				stopped = {};
			},
		};

		return service;
	},
]);

app.config([
	"$provide",
	function ($provide) {
		$provide.decorator("$http", [
			"$delegate",
			"LiveUpdateGuard",
			function ($delegate, LiveUpdateGuard) {
				function trackedHttp(requestConfig) {
					const promise = $delegate(requestConfig);
					LiveUpdateGuard.track(promise);
					return promise;
				}

				// $http.defaults and $http.pendingRequests stay the very objects the rest of Angular reads.
				angular.extend(trackedHttp, $delegate);

				// The shortcuts call the original $http internally, so they are rebuilt to go through this one.
				["get", "delete", "head", "jsonp"].forEach(function (method) {
					trackedHttp[method] = function (url, config) {
						return trackedHttp(angular.extend({}, config || {}, { method: method, url: url }));
					};
				});
				["post", "put", "patch"].forEach(function (method) {
					trackedHttp[method] = function (url, data, config) {
						return trackedHttp(angular.extend({}, config || {}, { method: method, url: url, data: data }));
					};
				});

				return trackedHttp;
			},
		]);
	},
]);
